import type {MdGlass} from "../glass/MdGlass";
import {FrmVar} from "../../drawfrm/FrmVar";
import type {MdFrameProfileType, MdFrameSpec} from "./MdFrameSpec";

export type MdFrameBlock = {
    id: string;
    kind: "column" | "row";

    // every block carries a size on its parent-flow axis (width inside a row,
    // height inside a column). Whether that size is a hard layout constraint or a
    // soft suggestion the solver may refine is decided by FrmVar.isModified() — an
    // unmodified size is a starting hint; a user-edited (modified) size is pinned.
    size: FrmVar;
    // block children MUST have a different kind than this block (no column inside a
    // column). Cells (items / blocks) alternate with sticks. A stick between two cells
    // is an interior mullion. A stick before the first cell or after the last cell is
    // the block's own edge stick: it lies on the frame outline across the block's flow
    // (a row's left/right jamb, a column's head/sill) and covers the cells of this block
    // on that side. The deepest block's edge stick wins over an enclosing block's. See
    // inline-outline-plan.md.
    children: MdFrameBlockChild[];
}

export type MdFrameJoint = "miter" | "extend" | "cap";

// the default termination for an outline corner stick given the frame-wide
// default. A miter applies to both sticks at the corner; otherwise the vertical
// jamb takes the default and the horizontal rail the complement, so the two stay
// complementary (one owns the corner, the other butts against it):
//   - default = cap    -> horizontals extend, verticals cap
//   - default = extend -> horizontals cap,    verticals extend
export function defaultCornerJoint(orientation: "horizontal" | "vertical", def: MdFrameJoint): MdFrameJoint {
    if(def === "miter") return "miter";
    if(orientation === "vertical") return def;
    return def === "cap" ? "extend" : "cap";
}

// the default termination for an outline corner (before any per-corner override),
// given the corner's vertical position and which of its two sticks this is. Bottom
// corners default to an extended jamb: the vertical jamb runs all the way to the
// floor (owns the corner) and the horizontal bottom run is backset to cap against
// it. This is fixed regardless of the frame-wide default; top corners follow the
// frame default (miter unless changed). The two sticks at a corner stay
// complementary either way.
export function cornerDefaultJoint(
    verticalSide: "top" | "bottom",
    orientation: "horizontal" | "vertical",
    frameDefault: MdFrameJoint,
): MdFrameJoint {
    const base: MdFrameJoint = verticalSide === "bottom" ? "extend" : frameDefault;
    return defaultCornerJoint(orientation, base);
}

// a cross line: the two collinear capping sticks (one in each neighbour cell)
// that align across a through stick. Used to drive the cross-flip transpose.
export type MdCrossLine = {
    idA: string;
    idB: string;
};

// runtime-derived joint info for a single stick (positions aren't in the model, so
// this is computed after layout — see scenes/frameGrid/Joints.ts):
//   - crossLines: cross lines where this stick is the *through* stick (so it's the
//     extended one; flipping it transposes its parent block). Empty otherwise.
//   - extendA / extendB: the through-stick id of a cross at this stick's A / B end,
//     where this stick is a *capping* one. Extending that end flips the cross.
//     (A = top end for a vertical stick, left end for a horizontal one; B the other.)
//   - isTee: this stick's end butts into a perpendicular run mid-span (forced cap)
//     and isn't part of a flippable cross.
export type MdStickJoint = {
    crossLines: MdCrossLine[];
    extendA?: string;
    extendB?: string;
    isTee: boolean;
};

// runtime-derived info for one outer corner of the frame outline: a horizontal and a
// vertical stick piece meeting at their ends (see scenes/frameGrid/Corners.ts). An end is
// `editable` when it is the model stick's own end, so a terminateA/B override applies to
// it; the end of a piece the renderer split off keeps its default.
export type MdCornerInfo = {
    key: string;
    label: string;
    h: {id: string; end: "A" | "B"; editable: boolean};
    v: {id: string; end: "A" | "B"; editable: boolean};
    locked: boolean;
    allowMiter: boolean;
    defaults: {h: MdFrameJoint; v: MdFrameJoint};
};

// runtime-derived size of a step (a void cell), in drawing units: `height` is the step's
// extent from the frame's outer edge to the step stick. `extension` is the part of it
// outside the void cell's own size; the void reaches the outer frame line by layout, so
// it is 0 (kept for the step dimension UI).
export type MdStepInfo = {height: number; extension: number};

export type MdFrameBlockChild = {
    kind: "item";
    item: MdFrameItem
} | {
    kind: "block";
    block: MdFrameBlock
} | {
    kind: "stick";
    stick: MdFrameStick
}

// a single separator/jamb piece — a stick of frame stock between two cells, or
// along the frame outline (a block's own edge stick). Rendered as a StickMaterial.
export type MdFrameStick = {
    id: string;

    // explicit thickness override. When omitted, the renderer uses jambFace. On an air
    // gap this is the size of the gap.
    thickness?: FrmVar;

    // an air gap (see BlockOps.makeAirGap): no frame stock between the two cells, only
    // the open space `thickness` wide. It is laid out like a stick, drawn as a dashed
    // outline, and takes no part in the joinery or the cut plan. Only an interior stick
    // with a cell on each side can be one; never a stick beside the door's leaf.
    airGap?: boolean;

    terminateA?: MdFrameJoint;
    terminateB?: MdFrameJoint;

    // per-stick frame-profile overrides, one per face (the face pointing left/−x and
    // the face pointing right/+x in the top-down section). An interior mullion faces a
    // cell on each side and can set both; a vertical outline jamb faces a cell only on
    // its inner side and sets that one. Undefined inherits the neighbouring cell's
    // profile, then the spec default. Read/write via BlockOps.getStickProfile /
    // setStickProfile; the editable faces come from getStickProfileFaces.
    profileLeft?: MdFrameProfileType;
    profileRight?: MdFrameProfileType;

    // the stick this one was joined into (see BlockOps.joinSticks) when the two can't
    // share one block: they draw as one stick, and their steps share one size.
    joinedTo?: string;

    // a sloped outline stick (see BlockOps.makeSloped): the cells under it are
    // `drop` shorter at `shortEnd` than at the other end. The layout stays
    // rectangular (the cell's size is the tall end); the slope is drawn inside it.
    slope?: MdStickSlope;

    // the plan-view angle of a double-sided floor-to-ceiling mullion, in degrees (see
    // BlockOps.makeAngled): the angle between the cell on its left and the cell on its
    // right, measured on the locationOne side. Undefined = 180 (a straight run). The
    // elevation draws the frame flat either way; only the top-down section turns here.
    angle?: FrmVar;

    // the frame anchors on an outline stick (see scenes/frameGrid/Anchors.ts). Undefined =
    // no anchors. A joined stick never holds them: its join root does.
    anchors?: MdStickAnchors;
}

export type MdStickAnchors = {
    // 1+, in A→B order along the stick.
    sections: MdAnchorSection[];
};

export type MdAnchorSection = {
    // fraction of the stick length; the fractions of a stick sum to 1.
    length: number;
    product: number;
    // undefined = auto (anchorAutoQty).
    qty?: number;
};

export type MdStickSlope = {
    // A is the left or top end, B the right or bottom one (as for terminateA/B).
    shortEnd: "A" | "B";
    drop: FrmVar;
};

// where the glass sits in the frame opening. Some options only work with a
// particular frame profile — see forcedProfile.
export type MdGlazing = "inside" | "outside" | "double" | "center" | "flushInside" | "flushOutside";

export const glazingLabels: Record<MdGlazing, string> = {
    "inside": "Glazed Inside",
    "outside": "Glazed Outside",
    "double": "Double Glazed",
    "center": "Center Glazed",
    "flushInside": "Flush Glazed Inside",
    "flushOutside": "Flush Glazed Outside",
};

export const defaultGlazing: MdGlazing = "inside";

// what a fixed panel is made of.
export type MdPanelMaterial = "metal" | "wood";

export const panelMaterialLabels: Record<MdPanelMaterial, string> = {
    "metal": "Metal",
    "wood": "Wood",
};

export const defaultPanelMaterial: MdPanelMaterial = "metal";

// where the panel sits in the frame opening.
export type MdPanelPosition = "inside" | "outside" | "center";

export const panelPositionLabels: Record<MdPanelPosition, string> = {
    "inside": "Inside",
    "outside": "Outside",
    "center": "Center",
};

export const defaultPanelPosition: MdPanelPosition = "inside";

export type MdFrameItem = {
    id: string;

    // the frame profile this item's surrounding frame uses. Doors always follow
    // MdFrameSpec.profile (any value here is ignored for them); other items inherit
    // the spec default when this is undefined and override it when the user sets one.
    // Resolve via itemProfile() rather than reading this directly.
    profile?: MdFrameProfileType;
} & ({
    kind: "doors"
    width: FrmVar;
    height: FrmVar;
} | {
    kind: "lite"
    size?: FrmVar;
    // the reference mark drawn on the lite ("P1", "P2", …; a door lite uses G). Assigned by
    // MdFrameSpec.assignLiteMarks and stable for the life of the lite, so anything that
    // cites a lite by mark keeps pointing at the same one. Empty only between the cell
    // being created and that assignment.
    mark?: string;
    // undefined = defaultGlazing. Read through itemGlazing().
    glazing?: MdGlazing;
    // undefined = the glass defaults. Read through liteGlass().
    glass?: MdGlass;
} | {
    // a solid fixed panel — sized like a lite, but not glazed.
    kind: "fixedPanel"
    size?: FrmVar;
    // both undefined = the defaults above. Read through itemPanelMaterial() /
    // itemPanelPosition().
    material?: MdPanelMaterial;
    position?: MdPanelPosition;
} | {
    // a space outside the frame (wall, block or wood) that makes a step in the frame
    // outline. Sized like a lite; the frame draws no stick along its outer edges.
    kind: "void"
    size?: FrmVar;
} | {
    // the sill under the door. Always the second child of a column whose first child
    // is the doors item (the door cell). `size` is its height, bound to
    // MdFrameSpec.thresholdHeight by wireDoorsItem. Not drawn in live mode; the door
    // face shows the threshold dimension. Never selectable, resizable or removable.
    kind: "threshold"
    size: FrmVar;
})

// the lite arm of MdFrameItem, for code that already knows it holds glass.
export type MdFrameLite = Extract<MdFrameItem, {kind: "lite"}>;
export type MdFrameDoors = Extract<MdFrameItem, {kind: "doors"}>;

export function itemGlazing(item: MdFrameItem): MdGlazing {
    return item.kind === "lite" ? (item.glazing ?? defaultGlazing) : defaultGlazing;
}

export function itemPanelMaterial(item: MdFrameItem): MdPanelMaterial {
    return item.kind === "fixedPanel" ? (item.material ?? defaultPanelMaterial) : defaultPanelMaterial;
}

export function itemPanelPosition(item: MdFrameItem): MdPanelPosition {
    return item.kind === "fixedPanel" ? (item.position ?? defaultPanelPosition) : defaultPanelPosition;
}

// the frame profile a fill configuration can only be built in, or undefined when it
// works with any profile. Applied to the cell and the sticks around it by
// BlockOps.applyForcedProfile.
export function forcedProfile(item: MdFrameItem): MdFrameProfileType | undefined {
    if(item.kind === "lite") {
        switch(itemGlazing(item)) {
            case "center": return "CO";
            case "flushInside": return "SR";
            case "flushOutside": return "SR";
            default: return undefined;
        }
    }

    if(item.kind === "fixedPanel" && itemPanelPosition(item) === "center") return "CO";
    return undefined;
}

// the effective frame profile for an item: doors always follow the spec default;
// every other item uses its per-item override when set, else the spec default.
export function itemProfile(spec: MdFrameSpec, item: MdFrameItem): MdFrameProfileType {
    if(item.kind === "doors") return spec.profile;
    return item.profile ?? spec.profile;
}

// true when the tree holds a threshold item.
export function hasThreshold(block: MdFrameBlock): boolean {
    return block.children.some(c =>
        c.kind === "item" ? c.item.kind === "threshold" :
        c.kind === "block" ? hasThreshold(c.block) : false);
}

// the step void behind each step stick, by stick id: the void after a block's last
// stick, or before its first.
export function stepVoids(root: MdFrameBlock): Map<string, MdFrameItem> {
    const out = new Map<string, MdFrameItem>();
    const walk = (block: MdFrameBlock) => {
        const c = block.children;
        const ends: [MdFrameBlockChild | undefined, MdFrameBlockChild | undefined][] = [
            [c[c.length - 1], c[c.length - 2]],
            [c[0], c[1]],
        ];
        for(const [end, next] of ends) {
            if(end?.kind === "item" && end.item.kind === "void" && next?.kind === "stick") out.set(next.stick.id, end.item);
        }
        for(const ch of c) if(ch.kind === "block") walk(ch.block);
    };
    walk(root);
    return out;
}

// every stick child in the tree, by id.
export function sticksById(root: MdFrameBlock): Map<string, MdFrameStick> {
    const out = new Map<string, MdFrameStick>();
    const walk = (block: MdFrameBlock) => {
        for(const c of block.children) {
            if(c.kind === "stick") out.set(c.stick.id, c.stick);
            if(c.kind === "block") walk(c.block);
        }
    };
    walk(root);
    return out;
}

// the stick a joined stick finally joins into (itself when it isn't joined).
export function joinedRootId(sticks: Map<string, MdFrameStick>, id: string): string {
    const seen = new Set<string>();
    let cur = id;
    for(;;) {
        const next = sticks.get(cur)?.joinedTo;
        if(!next || seen.has(next)) return cur;
        seen.add(cur);
        cur = next;
    }
}

// joined steps share one size: point each joined step's void at its root's size.
// The saved design stores the size on each, so call this after loading one.
export function shareJoinedStepSizes(root: MdFrameBlock) {
    const sticks = sticksById(root);
    const voids = stepVoids(root);
    for(const [id, item] of voids) {
        const rootVoid = voids.get(joinedRootId(sticks, id));
        if(!rootVoid || rootVoid === item || item.kind !== "void" || rootVoid.kind !== "void" || !rootVoid.size) continue;
        item.size = rootVoid.size;
    }
}
